import React, { memo, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Paper, Typography, Box, alpha, Chip } from '@mui/material';
import { Schedule } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const TimerNode = memo(({ data, selected, id }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Cyan theme colors
  const primaryColor = theme.palette.info.light; // Cyan
  const secondaryColor = theme.palette.info.dark; // Dark cyan
  const textColor = theme.palette.common.white;

  const handleDoubleClick = useCallback((event) => {
    event.stopPropagation();
    const editEvent = new CustomEvent('editNode', { detail: { nodeId: id } });
    window.dispatchEvent(editEvent);
  }, [id]);

  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    const contextEvent = new CustomEvent('nodeContextMenu', {
      detail: {
        nodeId: id,
        x: event.clientX,
        y: event.clientY
      }
    });
    window.dispatchEvent(contextEvent);
  }, [id]);

  // Format duration display
  const formatDuration = (duration) => {
    if (!duration) return 'Not set';
    if (duration.includes('cron:')) {
      return duration.replace('cron:', '');
    }
    return duration;
  };

  return (
    <Paper
      elevation={selected ? 12 : 6}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      sx={{
        width: 120,
        height: 120,
        borderRadius: '50%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        cursor: 'move',
        background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
        border: selected
          ? `3px solid ${theme.palette.secondary.main}`
          : `2px solid ${alpha(primaryColor, 0.3)}`,
        transition: theme.transitions.create([
          'transform', 'box-shadow', 'border'
        ], {
          duration: theme.transitions.duration.shorter,
        }),
        '&:hover': {
          transform: 'scale(1.08) rotate(5deg)',
          boxShadow: `0 8px 32px ${alpha(primaryColor, 0.4)}`,
          border: `3px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
          '& .timer-icon': {
            transform: 'scale(1.2) rotate(360deg)',
          },
          '& .pulse-ring': {
            animation: 'pulse-ring 1.5s cubic-bezier(0.215, 0.61, 0.355, 1) infinite',
          }
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -4,
          left: -4,
          right: -4,
          bottom: -4,
          borderRadius: '50%',
          background: `conic-gradient(from 0deg, ${primaryColor}, ${secondaryColor}, ${primaryColor})`,
          opacity: selected ? 0.6 : 0,
          transition: 'opacity 0.3s ease',
          zIndex: -1,
        },
        '@keyframes pulse-ring': {
          '0%': {
            transform: 'scale(1)',
            opacity: 1,
          },
          '100%': {
            transform: 'scale(1.3)',
            opacity: 0,
          },
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Timer node: ${data.label || 'Timer'}`}
      aria-selected={selected}
    >
      {/* Pulse ring effect */}
      <Box
        className="pulse-ring"
        sx={{
          position: 'absolute',
          top: -8,
          left: -8,
          right: -8,
          bottom: -8,
          borderRadius: '50%',
          border: `2px solid ${alpha(primaryColor, 0.6)}`,
          opacity: 0,
        }}
      />

      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(primaryColor, 0.3)}`,
        }}
      />

      {/* Main content */}
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        zIndex: 1,
      }}>
        <Schedule
          className="timer-icon"
          sx={{
            fontSize: 32,
            color: textColor,
            mb: 0.5,
            filter: `drop-shadow(0 2px 4px ${alpha(theme.palette.common.black, 0.3)})`,
            transition: 'transform 0.3s ease',
          }}
        />
        <Typography
          variant="caption"
          fontWeight="700"
          sx={{
            color: textColor,
            fontSize: '0.75rem',
            lineHeight: 1,
            textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.3)}`,
            maxWidth: '80px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {data.label || 'Timer'}
        </Typography>
      </Box>

      {/* Duration/Cron display */}
      {data.duration && (
        <Chip
          label={formatDuration(data.duration)}
          size="small"
          sx={{
            position: 'absolute',
            bottom: -20,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: alpha(primaryColor, 0.9),
            color: textColor,
            fontSize: '0.65rem',
            height: 20,
            maxWidth: 140,
            fontWeight: 600,
            border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
            backdropFilter: 'blur(4px)',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }
          }}
        />
      )}

      {/* Timer type indicator */}
      {data.timerType && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: -8,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: data.timerType === 'cron'
              ? `linear-gradient(135deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`
              : `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.common.white,
            fontSize: '0.6rem',
            fontWeight: 'bold',
            border: `2px solid ${theme.palette.background.paper}`,
            boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.2)}`,
          }}
        >
          {data.timerType === 'cron' ? 'C' : 'T'}
        </Box>
      )}

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
          border: `2px solid ${theme.palette.background.paper}`,
          width: 14,
          height: 14,
          boxShadow: `0 2px 8px ${alpha(primaryColor, 0.3)}`,
        }}
      />

      {/* Rotating clock hands effect */}
      <Box
        sx={{
          position: 'absolute',
          width: 2,
          height: 15,
          backgroundColor: alpha(textColor, 0.7),
          top: '30%',
          left: '50%',
          transformOrigin: 'bottom center',
          transform: 'translateX(-50%)',
          animation: 'clock-rotation 60s linear infinite',
          '@keyframes clock-rotation': {
            '0%': {
              transform: 'translateX(-50%) rotate(0deg)',
            },
            '100%': {
              transform: 'translateX(-50%) rotate(360deg)',
            },
          }
        }}
      />
    </Paper>
  );
});

TimerNode.displayName = 'TimerNode';

export default TimerNode;